import { getAuthToken, getAuthHeaders } from '@/utils/auth';
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/context/AuthContext';
import { Sun, Moon, Settings, Home, Building2, Car, MessageCircle, Heart, User, LogOut, Bell, Lock, Palette, Globe, DollarSign, Check, Search, ArrowLeft } from 'lucide-react';
import Nav from '@/components/Nav';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Full list of countries (ISO)
const COUNTRIES = [
  "United Arab Emirates", "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia", 
  "Austria", "Azerbaijan", "Bahrain", "Bangladesh", "Belarus", "Belgium", "Bhutan", "Bolivia", "Bosnia and Herzegovina", 
  "Brazil", "Brunei", "Bulgaria", "Cambodia", "Cameroon", "Canada", "Chile", "China", "Colombia", "Costa Rica", 
  "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Ecuador", "Egypt", "Estonia", "Ethiopia", "Finland", 
  "France", "Georgia", "Germany", "Ghana", "Greece", "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", 
  "Iran", "Iraq", "Ireland", "Israel", "Italy", "Japan", "Jordan", "Kazakhstan", "Kenya", "Kuwait", "Kyrgyzstan", 
  "Latvia", "Lebanon", "Libya", "Lithuania", "Luxembourg", "Malaysia", "Maldives", "Malta", "Mexico", "Monaco", 
  "Mongolia", "Montenegro", "Morocco", "Myanmar", "Nepal", "Netherlands", "New Zealand", "Nigeria", "North Macedonia", 
  "Norway", "Oman", "Pakistan", "Palestine", "Panama", "Peru", "Philippines", "Poland", "Portugal", "Qatar", 
  "Romania", "Russia", "Saudi Arabia", "Serbia", "Singapore", "Slovakia", "Slovenia", "South Africa", "South Korea", 
  "Spain", "Sri Lanka", "Sudan", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", 
  "Tunisia", "Turkey", "Turkmenistan", "Uganda", "Ukraine", "United Kingdom", "United States", "Uruguay", "Uzbekistan", 
  "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe"
].sort();

// Currency options
const CURRENCIES = [
  { code: "AED", name: "UAE Dirham", symbol: "د.إ" },
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "SAR", name: "Saudi Riyal", symbol: "﷼" },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme, setLightTheme, setDarkTheme } = useTheme();
  const { logout } = useAuth();
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  
  // Profile form values
  const [country, setCountry] = useState('');
  const [currency, setCurrency] = useState('AED');
  
  // Fetch user profile on mount
  useEffect(() => {
    fetchProfile();
  }, []);
  
  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/profile`);
      if (response.data) {
        setProfile(response.data);
        setCountry(response.data.personal?.country || '');
        setCurrency(response.data.personal?.currency || 'AED');
      }
    } catch (error) {
      if (error.response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      // Update personal section with new country and currency
      const updatedProfile = {
        ...profile,
        personal: {
          ...profile?.personal,
          country,
          currency,
        },
        home_preferences: profile?.home_preferences || {},
        lifestyle_preferences: profile?.lifestyle_preferences || {},
      };
      
      await axios.post(`${API}/onboarding/profile`, updatedProfile);
      toast.success('Profile updated successfully!');
      setProfile(updatedProfile);
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };
  
  // Filter countries based on search
  const filteredCountries = COUNTRIES.filter(c => 
    c.toLowerCase().includes(countrySearch.toLowerCase())
  );

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className={`min-h-screen mobile-pb ${theme === 'light' ? 'bg-gray-50 text-gray-900' : 'bg-obsidian text-text-primary'}`}>
      {/* Navigation */}
      <Nav activePage="/settings" />

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button variant="ghost" onClick={() => navigate(-1)} className={`mb-4 -ml-2 ${theme === 'light' ? 'text-gray-600 hover:text-gold' : 'text-text-secondary hover:text-gold'}`} data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <h1 className="font-playfair text-4xl font-semibold mb-4 flex items-center gap-3">
            <Settings className="w-10 h-10 text-gold" />
            Settings
          </h1>
          <p className={`text-lg ${theme === 'light' ? 'text-gray-600' : 'text-text-secondary'}`}>
            Customize your ASTIIV BASGAT experience
          </p>
        </motion.div>

        {/* Theme Settings */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className={`${theme === 'light' ? 'bg-white border-gray-200' : 'glass-card border-white/10'} border rounded-xl p-6 mb-6`}
        >
          <div className="flex items-center gap-3 mb-6">
            <Palette className="w-6 h-6 text-gold" />
            <h2 className="font-playfair text-2xl font-semibold">Appearance</h2>
          </div>
          
          <div className="space-y-6">
            <div>
              <label className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-text-secondary'} mb-4 block`}>
                Choose your preferred theme
              </label>
              
              <div className="grid grid-cols-2 gap-4">
                {/* Light Mode Option */}
                <button
                  onClick={setLightTheme}
                  className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                    theme === 'light' 
                      ? 'border-gold bg-gold/10' 
                      : 'border-white/10 hover:border-gold/50'
                  }`}
                  data-testid="theme-light-btn"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
                      <Sun className="w-8 h-8 text-yellow-500" />
                    </div>
                    <div>
                      <div className="font-semibold text-lg">Light Mode</div>
                      <div className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                        Clean, bright interface
                      </div>
                    </div>
                    {theme === 'light' && (
                      <div className="text-gold text-sm font-medium">✓ Active</div>
                    )}
                  </div>
                </button>

                {/* Dark Mode Option */}
                <button
                  onClick={setDarkTheme}
                  className={`p-6 rounded-xl border-2 transition-all duration-300 ${
                    theme === 'dark' 
                      ? 'border-gold bg-gold/10' 
                      : 'border-white/10 hover:border-gold/50'
                  }`}
                  data-testid="theme-dark-btn"
                >
                  <div className="flex flex-col items-center gap-3">
                    <div className={`w-16 h-16 rounded-full ${theme === 'light' ? 'bg-gray-800' : 'bg-white/10'} flex items-center justify-center`}>
                      <Moon className="w-8 h-8 text-blue-400" />
                    </div>
                    <div>
                      <div className="font-semibold text-lg">Dark Mode</div>
                      <div className={`text-sm ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                        Elegant, luxury feel
                      </div>
                    </div>
                    {theme === 'dark' && (
                      <div className="text-gold text-sm font-medium">✓ Active</div>
                    )}
                  </div>
                </button>
              </div>
            </div>

            {/* Quick Toggle */}
            <div className={`flex items-center justify-between p-4 rounded-lg ${theme === 'light' ? 'bg-gray-100' : 'bg-white/5'}`}>
              <div className="flex items-center gap-3">
                {theme === 'dark' ? <Moon className="w-5 h-5 text-blue-400" /> : <Sun className="w-5 h-5 text-yellow-500" />}
                <span>Current: {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
              </div>
              <Button
                onClick={toggleTheme}
                variant="outline"
                className={`${theme === 'light' ? 'border-gray-300 hover:border-gold' : 'border-white/20 hover:border-gold'}`}
                data-testid="theme-toggle-btn"
              >
                Switch to {theme === 'dark' ? 'Light' : 'Dark'}
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Personal Profile Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`${theme === 'light' ? 'bg-white border-gray-200' : 'glass-card border-white/10'} border rounded-xl p-6 mb-6`}
        >
          <div className="flex items-center gap-3 mb-6">
            <User className="w-6 h-6 text-gold" />
            <h2 className="font-playfair text-2xl font-semibold">Personal Profile</h2>
          </div>
          
          {loading ? (
            <div className={`text-center py-8 ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
              Loading profile...
            </div>
          ) : (
            <div className="space-y-6">
              {/* Country Selection */}
              <div>
                <label className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-text-secondary'} mb-2 block flex items-center gap-2`}>
                  <Globe className="w-4 h-4" />
                  Country of Residence
                </label>
                <div className="relative">
                  <div 
                    className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer ${
                      theme === 'light' 
                        ? 'bg-gray-50 border-gray-200 hover:border-gold' 
                        : 'bg-white/5 border-white/10 hover:border-gold/50'
                    }`}
                    onClick={() => setShowCountryDropdown(!showCountryDropdown)}
                    data-testid="country-select-trigger"
                  >
                    <Search className="w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={showCountryDropdown ? countrySearch : country || 'Select your country'}
                      onChange={(e) => setCountrySearch(e.target.value)}
                      onFocus={() => setShowCountryDropdown(true)}
                      placeholder="Search countries..."
                      className={`flex-1 bg-transparent outline-none ${
                        !country && !showCountryDropdown ? 'text-gray-400' : ''
                      } ${theme === 'light' ? 'text-gray-900' : 'text-white'}`}
                      data-testid="country-search-input"
                    />
                  </div>
                  
                  {showCountryDropdown && (
                    <div 
                      className={`absolute z-50 mt-2 w-full max-h-60 overflow-y-auto rounded-lg border shadow-xl ${
                        theme === 'light' 
                          ? 'bg-white border-gray-200' 
                          : 'bg-neutral-900 border-white/10'
                      }`}
                    >
                      {filteredCountries.length > 0 ? (
                        filteredCountries.map((c) => (
                          <button
                            key={c}
                            onClick={() => {
                              setCountry(c);
                              setCountrySearch('');
                              setShowCountryDropdown(false);
                            }}
                            className={`w-full text-left px-4 py-3 flex items-center justify-between ${
                              theme === 'light'
                                ? 'hover:bg-gray-100 text-gray-900'
                                : 'hover:bg-white/10 text-white'
                            } ${country === c ? 'bg-gold/20' : ''}`}
                            data-testid={`country-option-${c.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            <span>{c}</span>
                            {country === c && <Check className="w-4 h-4 text-gold" />}
                          </button>
                        ))
                      ) : (
                        <div className={`px-4 py-3 ${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
                          No countries found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Currency Selection */}
              <div>
                <label className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-text-secondary'} mb-2 block flex items-center gap-2`}>
                  <DollarSign className="w-4 h-4" />
                  Preferred Currency
                </label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger 
                    className={`w-full ${
                      theme === 'light' 
                        ? 'bg-gray-50 border-gray-200 text-gray-900' 
                        : 'bg-white/5 border-white/10 text-white'
                    }`}
                    data-testid="currency-select-trigger"
                  >
                    <SelectValue placeholder="Select currency" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-gray-200">
                    {CURRENCIES.map((curr) => (
                      <SelectItem 
                        key={curr.code} 
                        value={curr.code}
                        className="text-gray-900 hover:bg-gray-100"
                        data-testid={`currency-option-${curr.code.toLowerCase()}`}
                      >
                        <span className="flex items-center gap-2">
                          <span className="font-mono text-lg">{curr.symbol}</span>
                          <span>{curr.code} - {curr.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Save Button */}
              <div className="pt-4">
                <Button 
                  onClick={handleSaveProfile}
                  disabled={saving}
                  className="bg-gold text-black hover:bg-gold-light w-full sm:w-auto"
                  data-testid="save-profile-btn"
                >
                  {saving ? 'Saving...' : 'Save Profile Changes'}
                </Button>
              </div>
            </div>
          )}
        </motion.div>

        {/* Notifications Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className={`${theme === 'light' ? 'bg-white border-gray-200' : 'glass-card border-white/10'} border rounded-xl p-6 mb-6`}
        >
          <div className="flex items-center gap-3 mb-6">
            <Bell className="w-6 h-6 text-gold" />
            <h2 className="font-playfair text-2xl font-semibold">Notifications</h2>
          </div>
          <p className={`${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
            Notification preferences coming soon...
          </p>
        </motion.div>

        {/* Privacy & Security Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className={`${theme === 'light' ? 'bg-white border-gray-200' : 'glass-card border-white/10'} border rounded-xl p-6`}
        >
          <div className="flex items-center gap-3 mb-6">
            <Lock className="w-6 h-6 text-gold" />
            <h2 className="font-playfair text-2xl font-semibold">Privacy & Security</h2>
          </div>
          <p className={`${theme === 'light' ? 'text-gray-500' : 'text-text-secondary'}`}>
            Privacy settings coming soon...
          </p>
        </motion.div>
      </main>
    </div>
  );
};

export default SettingsPage;
